import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Breadcrumbs,
  Link,
  Chip,
  Button,
  Tooltip,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Paper,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  AccountCircle as AccountIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  Speed as SpeedIcon,
  Schedule as ScheduleIcon,
  Message as MessageIcon,
  Task as TaskIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useEnhancedNavigation } from '../../hooks/useEnhancedNavigation';

interface DashboardHeaderProps {
  drawerWidth: number;
  onDrawerToggle: () => void;
  title?: string;
}

/**
 * Enhanced Dashboard Header Component
 * Features breadcrumbs, quick actions, real-time notifications, and performance metrics
 */
const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  drawerWidth,
  onDrawerToggle,
  title,
}) => {
  const theme = useTheme();
  const { user, logout, getDashboardPath } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    metrics,
    stats,
    loading,
    getQuickActions,
    markAsRead,
    addToFavorites,
    refreshMetrics,
  } = useEnhancedNavigation();

  // Menu states
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [notificationsAnchor, setNotificationsAnchor] = useState<null | HTMLElement>(null);
  const [quickActionsAnchor, setQuickActionsAnchor] = useState<null | HTMLElement>(null);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleNotificationsOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchor(event.currentTarget);
  };

  const handleQuickActionsOpen = (event: React.MouseEvent<HTMLElement>) => {
    setQuickActionsAnchor(event.currentTarget);
  };

  const handleLogout = () => {
    logout();
    setUserMenuAnchor(null);
  };

  /**
   * Generate breadcrumbs from current path
   */
  const generateBreadcrumbs = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: Array<{
      label: string;
      path: string;
      icon?: React.ReactNode;
      isLast?: boolean;
    }> = [
      {
        label: 'Dashboard',
        path: getDashboardPath(),
        icon: <HomeIcon sx={{ fontSize: 16 }} />,
      },
    ];

    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      if (segment === 'dashboard') return;
      
      currentPath += `/${segment}`;
      const isLast = index === pathSegments.length - 1;
      
      breadcrumbs.push({
        label: segment.charAt(0).toUpperCase() + segment.slice(1).replace(/[-_]/g, ' '),
        path: `/dashboard${currentPath}`,
        isLast,
      });
    });

    return breadcrumbs;
  };

  /**
   * Get page title from path or custom title
   */
  const getPageTitle = () => {
    if (title) return title;
    
    const path = location.pathname;
    if (path === '/dashboard' || path === '/') {
      return `${user?.role?.replace('_', ' ').toUpperCase() || 'User'} Dashboard`;
    }
    
    const segments = path.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1];
    return lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1).replace(/[-_]/g, ' ');
  };

  const breadcrumbs = generateBreadcrumbs();
  const quickActions = getQuickActions();
  const totalNotifications = metrics.notifications + metrics.unreadMessages + metrics.systemAlerts;

  return (
    <AppBar
      position="fixed"
      elevation={1}
      sx={{
        width: { md: `calc(100% - ${drawerWidth}px)` },
        ml: { md: `${drawerWidth}px` },
        bgcolor: 'background.paper',
        color: 'text.primary',
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 64, sm: 70 }, px: { xs: 2, sm: 3 } }}>
        {/* Mobile Menu Button */}
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={onDrawerToggle}
          sx={{ mr: 2, display: { md: 'none' } }}
        >
          <MenuIcon />
        </IconButton>

        {/* Page Title and Breadcrumbs */}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            noWrap
            component="h1"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              mb: 0.5,
            }}
          >
            {getPageTitle()}
          </Typography>
          
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={{
              display: { xs: 'none', sm: 'flex' },
              '& .MuiBreadcrumbs-ol': {
                flexWrap: 'nowrap',
              },
            }}
          >
            {breadcrumbs.map((crumb, index) => (
              <Link
                key={crumb.path}
                component="button"
                variant="caption"
                onClick={() => !crumb.isLast && navigate(crumb.path)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: crumb.isLast ? 'text.primary' : 'text.secondary',
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                  fontWeight: crumb.isLast ? 500 : 400,
                  cursor: crumb.isLast ? 'default' : 'pointer',
                  '&:hover': {
                    color: !crumb.isLast ? 'primary.main' : 'text.primary',
                  },
                }}
              >
                {crumb.icon}
                {crumb.label}
              </Link>
            ))}
          </Breadcrumbs>
        </Box>

        {/* Performance Indicator */}
        <Tooltip title="System Performance">
          <Chip
            icon={<SpeedIcon />}
            label="99.8%"
            size="small"
            color="success"
            variant="outlined"
            sx={{ mr: 1, display: { xs: 'none', sm: 'flex' } }}
          />
        </Tooltip>

        {/* Quick Actions Button */}
        <Tooltip title="Quick Actions">
          <IconButton
            onClick={handleQuickActionsOpen}
            sx={{ mr: 1 }}
          >
            <SpeedIcon />
          </IconButton>
        </Tooltip>

        {/* Refresh Button */}
        <Tooltip title="Refresh Data">
          <IconButton
            onClick={refreshMetrics}
            disabled={loading}
            sx={{ mr: 1 }}
          >
            {loading ? (
              <CircularProgress size={20} />
            ) : (
              <RefreshIcon />
            )}
          </IconButton>
        </Tooltip>

        {/* Notifications */}
        <Tooltip title="Notifications">
          <IconButton
            onClick={handleNotificationsOpen}
            sx={{ mr: 1 }}
          >
            <Badge badgeContent={totalNotifications} color="error" max={99}>
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* User Menu */}
        <Tooltip title="Account">
          <IconButton
            onClick={handleUserMenuOpen}
            size="small"
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
              {user?.name?.charAt(0) || <AccountIcon />}
            </Avatar>
          </IconButton>
        </Tooltip>

        {/* Quick Actions Popover */}
        <Popover
          open={Boolean(quickActionsAnchor)}
          anchorEl={quickActionsAnchor}
          onClose={() => setQuickActionsAnchor(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Paper sx={{ p: 2, minWidth: 250 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Quick Actions
            </Typography>
            <List dense>
              {quickActions.map((action, index) => (
                <ListItem
                  key={index}
                  disablePadding
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemButton
                    onClick={() => {
                      navigate(action.path);
                      setQuickActionsAnchor(null);
                    }}
                    sx={{ borderRadius: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Box sx={{ fontSize: '1.2rem' }}>{action.icon}</Box>
                    </ListItemIcon>
                    <ListItemText primary={action.label} />
                    {action.count !== undefined && action.count > 0 && (
                      <Chip
                        label={action.count}
                        size="small"
                        color="primary"
                        sx={{ height: 20, fontSize: '0.75rem' }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Popover>

        {/* Notifications Popover */}
        <Popover
          open={Boolean(notificationsAnchor)}
          anchorEl={notificationsAnchor}
          onClose={() => setNotificationsAnchor(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Paper sx={{ minWidth: 300, maxWidth: 400 }}>
            <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Notifications
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {totalNotifications} unread
              </Typography>
            </Box>
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {/* Messages */}
              {metrics.unreadMessages > 0 && (
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => {
                      navigate('/dashboard/communication/messages');
                      markAsRead('messages');
                      setNotificationsAnchor(null);
                    }}
                  >
                    <ListItemIcon>
                      <MessageIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${metrics.unreadMessages} new messages`}
                      secondary="Click to view"
                    />
                  </ListItemButton>
                </ListItem>
              )}
              
              {/* Tasks */}
              {metrics.pendingTasks > 0 && (
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => {
                      navigate('/dashboard/tasks');
                      markAsRead('tasks');
                      setNotificationsAnchor(null);
                    }}
                  >
                    <ListItemIcon>
                      <TaskIcon color="warning" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${metrics.pendingTasks} pending tasks`}
                      secondary="Requires attention"
                    />
                  </ListItemButton>
                </ListItem>
              )}
              
              {/* System Alerts */}
              {metrics.systemAlerts > 0 && (
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => {
                      navigate('/dashboard/monitor');
                      setNotificationsAnchor(null);
                    }}
                  >
                    <ListItemIcon>
                      <SpeedIcon color="error" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${metrics.systemAlerts} system alerts`}
                      secondary="Check system status"
                    />
                  </ListItemButton>
                </ListItem>
              )}
              
              {totalNotifications === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No new notifications"
                    secondary="You're all caught up!"
                    sx={{ textAlign: 'center' }}
                  />
                </ListItem>
              )}
            </List>
            
            {totalNotifications > 0 && (
              <>
                <Divider />
                <Box sx={{ p: 1 }}>
                  <Button
                    fullWidth
                    size="small"
                    onClick={() => {
                      markAsRead('notifications', totalNotifications);
                      setNotificationsAnchor(null);
                    }}
                  >
                    Mark All as Read
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        </Popover>

        {/* User Menu */}
        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={() => setUserMenuAnchor(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {user?.name || 'User'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
          <Divider />
          <MenuItem
            onClick={() => {
              navigate('/dashboard/profile');
              setUserMenuAnchor(null);
            }}
          >
            <AccountIcon sx={{ mr: 1 }} /> Profile
          </MenuItem>
          <MenuItem
            onClick={() => {
              navigate('/dashboard/settings');
              setUserMenuAnchor(null);
            }}
          >
            <SettingsIcon sx={{ mr: 1 }} /> Settings
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
            <LogoutIcon sx={{ mr: 1 }} /> Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default DashboardHeader;